/**
 * @vra/canonical — constrained-JCS canonicalization (profile vra-constrained-jcs-v1).
 *
 * RFC 8785 restricted per spec §5.3 (no floats on the ledger):
 *  - objects: ASCII string keys, sorted by code unit; arrays; strings (any
 *    Unicode, hashed as UTF-8); booleans; null
 *  - numbers: integers with |n| <= 2^53 - 1 only
 * Anything else throws. Every implementation (TS, Go, Python) must produce
 * byte-identical output — enforced by testvectors/canonical.json.
 */
import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex } from "@noble/hashes/utils";
const SHORT_ESCAPES = {
    "\b": "\\b",
    "\t": "\\t",
    "\n": "\\n",
    "\f": "\\f",
    "\r": "\\r",
    '"': '\\"',
    "\\": "\\\\",
};
function escString(s) {
    let out = '"';
    for (const ch of s) {
        const esc = SHORT_ESCAPES[ch];
        if (esc !== undefined) {
            out += esc;
        }
        else if (ch.codePointAt(0) < 0x20) {
            out += "\\u" + ch.codePointAt(0).toString(16).padStart(4, "0");
        }
        else {
            out += ch;
        }
    }
    return out + '"';
}
function isAscii(s) {
    for (let i = 0; i < s.length; i++)
        if (s.charCodeAt(i) > 0x7f)
            return false;
    return true;
}
/** Canonical JSON text of a value. Throws on floats, unsafe ints, non-ASCII keys. */
export function canonicalize(v) {
    if (v === null)
        return "null";
    if (v === true)
        return "true";
    if (v === false)
        return "false";
    if (typeof v === "number") {
        if (!Number.isSafeInteger(v)) {
            throw new Error(`only safe integers permitted (spec 5.3), got: ${v}`);
        }
        // -0 normalizes to 0
        return String(v === 0 ? 0 : v);
    }
    if (typeof v === "string")
        return escString(v);
    if (Array.isArray(v))
        return "[" + v.map(canonicalize).join(",") + "]";
    if (typeof v === "object") {
        const keys = Object.keys(v);
        for (const k of keys) {
            if (!isAscii(k))
                throw new Error(`object keys must be ASCII: ${k}`);
        }
        keys.sort(); // ASCII code-unit order == UTF-16 order for ASCII
        return ("{" +
            keys.map((k) => escString(k) + ":" + canonicalize(v[k])).join(",") +
            "}");
    }
    throw new Error(`unsupported value of type ${typeof v}`);
}
/** UTF-8 bytes of the canonical form — the exact bytes that get signed. */
export function canonicalBytes(v) {
    return new TextEncoder().encode(canonicalize(v));
}
/** Lowercase-hex SHA-256 of the canonical UTF-8 bytes — the artefact digest (§4.1.2). */
export function canonicalSha256Hex(v) {
    return bytesToHex(sha256(canonicalBytes(v)));
}
/** Raw 32-byte SHA-256 of the canonical bytes — input to ECDSA signing. */
export function canonicalSha256(v) {
    return sha256(canonicalBytes(v));
}
